// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { Box, Flex, Switch, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';
import { BendDimensionInput } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendDimensionInput';
import { BendDimInputValidationMessage } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendDimInputValidationMessage';
import { BendValidationStatus } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendValidationStatus';
import { BendYbcGrid } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendYbcGrid';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';

export const BendDimensionInputs = () => {
  const {
    setFieldValue,
    values: { bendType: currentBendType },
  } = useBendCreatorFormContext();

  const { wrappedGetInputProps, validateBend } = useBendCreatorValidationContext();

  const { bendTypes } = useBendTypes();
  const currentBendTypeData = useMemo(
    () => bendTypes?.find(({ name }) => name === currentBendType) ?? null,
    [currentBendType, bendTypes],
  );

  const isCustomBend = currentBendType === 'Custom';

  return (
    <>
      {!isCustomBend && (
        <Flex>
          <AgGridStyleTooltip
            label="When enabled, the leg length of the bend will adjust to the maximum length available based on the material stock length"
            withArrow
            openDelay={100}
          >
            <Switch
              label={
                <Flex gap={6} align="center">
                  Maximize Leg Length
                  <EvolveIcon icon="Info" size="sm" inline color="dimmed" />
                </Flex>
              }
              {...wrappedGetInputProps('maximizeLegLength', { type: 'checkbox', validateOnChange: true })}
              onChange={(e) => {
                const isChecked = e.currentTarget.checked;
                setFieldValue('maximizeLegLength', isChecked);
                void validateBend('maximizeLegLength', isChecked);
              }}
            />
          </AgGridStyleTooltip>
        </Flex>
      )}
      {isCustomBend ? (
        <BendYbcGrid />
      ) : isNotNil(currentBendTypeData) ? (
        currentBendTypeData.inputs.map((bendTypeInput) => (
          <Box key={bendTypeInput.name}>
            <BendDimensionInput
              bendTypeInput={bendTypeInput}
              isStockLengthField={currentBendTypeData.stockLengthKeyName === bendTypeInput.keyName}
            />
            <BendDimInputValidationMessage bendTypeInput={bendTypeInput} />
          </Box>
        ))
      ) : (
        <Text c="dimmed" fz="sm">
          Select a bend type.
        </Text>
      )}
      <BendValidationStatus currentBendTypeData={currentBendTypeData} />
    </>
  );
};
